'use client';

import { useSession } from 'next-auth/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';

import { syncMessages } from '@/features/messages/api';
import type { MessageRealtimeEvent } from '@/features/messages/types';
import { getUnreadCount, markNotificationsSeen, MESSAGE_API_URL, syncNotifications } from './api';
import { NotificationsContext } from './notifications-context';
import type { NotificationItem, UnreadCount } from './types';

const EMPTY_UNREAD: UnreadCount = {
  notifications: 0,
  messages: 0,
  total: 0,
  display: null,
};

function sequenceStorageKey(userId: number): string {
  return `message-api:last-notification-sequence:${userId}`;
}

function messageSequenceStorageKey(userId: number): string {
  return `message-api:last-message-sequence:${userId}`;
}

const MESSAGE_SOCKET_EVENTS = [
  'message.pending',
  'message.created',
  'message.status.updated',
  'messages.read',
  'conversation.created',
  'conversation.updated',
  'presence.updated',
  'typing.updated',
  'block.updated',
  'restriction.updated',
  'message.preview.updated',
  'link_preview.updated',
] as const;

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession();
  const accessToken = session?.accessToken;
  const userId = session?.user?.id;
  const [unread, setUnread] = useState<UnreadCount>(EMPTY_UNREAD);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [messageRefreshVersion, setMessageRefreshVersion] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const messageListenersRef = useRef(new Set<(event: MessageRealtimeEvent) => void>());
  const activeConversationRef = useRef<string | null>(null);
  const sessionGenerationRef = useRef(0);
  const unreadRequestGenerationRef = useRef(0);

  useEffect(() => {
    const generation = ++sessionGenerationRef.current;
    unreadRequestGenerationRef.current += 1;
    return () => {
      unreadRequestGenerationRef.current += 1;
      if (sessionGenerationRef.current === generation) sessionGenerationRef.current += 1;
    };
  }, [accessToken, status, userId]);

  const dispatchMessageEvent = useCallback((event: MessageRealtimeEvent) => {
    for (const listener of messageListenersRef.current) listener(event);
    setMessageRefreshVersion((value) => value + 1);
  }, []);

  const subscribeToMessages = useCallback((listener: (event: MessageRealtimeEvent) => void) => {
    messageListenersRef.current.add(listener);
    return () => messageListenersRef.current.delete(listener);
  }, []);

  const emitMessageSocketEvent = useCallback((event: string, payload: unknown) => {
    socketRef.current?.emit(event, payload);
  }, []);

  const sendClientState = useCallback(() => {
    socketRef.current?.emit('conversation.focus', {
      conversationId: activeConversationRef.current,
      active: document.visibilityState === 'visible' && document.hasFocus(),
    });
  }, []);

  const setActiveConversation = useCallback(
    (conversationId: string | null) => {
      activeConversationRef.current = conversationId;
      sendClientState();
    },
    [sendClientState],
  );

  const rememberSequence = useCallback(
    (sequence: number) => {
      if (!userId || !Number.isSafeInteger(sequence) || sequence < 0) return;
      const key = sequenceStorageKey(userId);
      const previous = Number(window.localStorage.getItem(key) ?? 0);
      if (sequence > previous) {
        window.localStorage.setItem(key, String(sequence));
      }
    },
    [userId],
  );

  const refreshUnread = useCallback(async () => {
    const sessionGeneration = sessionGenerationRef.current;
    const requestGeneration = ++unreadRequestGenerationRef.current;
    if (!accessToken) {
      if (
        sessionGeneration === sessionGenerationRef.current &&
        requestGeneration === unreadRequestGenerationRef.current
      ) {
        setUnread(EMPTY_UNREAD);
      }
      return;
    }
    try {
      const count = await getUnreadCount(accessToken);
      if (
        sessionGeneration === sessionGenerationRef.current &&
        requestGeneration === unreadRequestGenerationRef.current
      ) {
        setUnread(count);
      }
    } catch {
      // Сохраняем последнее подтверждённое значение до reconnect или следующего focus.
    }
  }, [accessToken]);

  const markSeen = useCallback(
    async (ids: string[]) => {
      if (!accessToken || ids.length === 0) return [];
      const sessionGeneration = sessionGenerationRef.current;
      const requestGeneration = ++unreadRequestGenerationRef.current;
      const result = await markNotificationsSeen(accessToken, [...new Set(ids)]);
      if (sessionGeneration !== sessionGenerationRef.current) return [];
      if (requestGeneration === unreadRequestGenerationRef.current) setUnread(result.unread);
      return result.seenIds;
    },
    [accessToken],
  );

  useEffect(() => {
    if (status !== 'authenticated' || !accessToken || !userId) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      const sessionGeneration = sessionGenerationRef.current;
      const requestGeneration = ++unreadRequestGenerationRef.current;
      const resetTimer = window.setTimeout(() => {
        if (
          sessionGeneration === sessionGenerationRef.current &&
          requestGeneration === unreadRequestGenerationRef.current
        ) {
          setUnread(EMPTY_UNREAD);
          setIsConnected(false);
        }
      }, 0);
      return () => window.clearTimeout(resetTimer);
    }

    let cancelled = false;
    let syncInProgress = false;
    let messageSyncInProgress = false;
    const storageKey = sequenceStorageKey(userId);
    const messageStorageKey = messageSequenceStorageKey(userId);

    const synchronize = async () => {
      if (syncInProgress || cancelled) return;
      syncInProgress = true;
      try {
        let after = Number(window.localStorage.getItem(storageKey) ?? 0);
        let hasMore = true;
        let receivedAny = false;

        while (hasMore && !cancelled) {
          const result = await syncNotifications(accessToken, after);
          if (cancelled) return;
          hasMore = result.hasMore;
          receivedAny ||= result.data.length > 0;
          after = Math.max(after, result.lastSequence);
          window.localStorage.setItem(storageKey, String(after));
        }

        if (receivedAny && !cancelled) {
          setRefreshVersion((value) => value + 1);
        }
        if (!cancelled) await refreshUnread();
      } catch {
        // Следующий reconnect или фокус окна повторит синхронизацию.
      } finally {
        syncInProgress = false;
      }
    };

    const synchronizeMessages = async () => {
      if (messageSyncInProgress || cancelled) return;
      messageSyncInProgress = true;
      try {
        let after = Number(window.sessionStorage.getItem(messageStorageKey) ?? 0);
        let hasMore = true;
        while (hasMore && !cancelled) {
          const result = await syncMessages(accessToken, after);
          if (cancelled) return;
          for (const event of result.data) {
            dispatchMessageEvent({ type: event.type, payload: event });
          }
          hasMore = result.hasMore;
          after = Math.max(after, result.lastSequence);
          window.sessionStorage.setItem(messageStorageKey, String(after));
        }
      } catch {
        // Следующий reconnect или фокус окна повторит синхронизацию сообщений.
      } finally {
        messageSyncInProgress = false;
      }
    };

    const initialRefreshTimer = window.setTimeout(() => void refreshUnread(), 0);

    const socket = io(`${MESSAGE_API_URL}/ws`, {
      auth: { token: accessToken },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 500,
      reconnectionDelayMax: 10_000,
    });
    socketRef.current = socket;

    socket.on('connection.ready', () => {
      if (cancelled) return;
      setIsConnected(true);
      void synchronize();
      void synchronizeMessages();
      sendClientState();
    });
    socket.on('disconnect', () => {
      if (!cancelled && socketRef.current === socket) setIsConnected(false);
    });
    socket.on('connect_error', () => {
      if (!cancelled && socketRef.current === socket) setIsConnected(false);
    });
    socket.on('notification.created', (notification: NotificationItem) => {
      if (cancelled) return;
      rememberSequence(notification.sequence);
      setRefreshVersion((value) => value + 1);
    });
    socket.on('unread_count.updated', () => {
      if (cancelled) return;
      // Socket snapshots from concurrent send/read flows may arrive out of order.
      // Always resolve the current authoritative value instead of trusting the payload.
      void refreshUnread();
    });
    socket.on('relationship.updated', (event: unknown) => {
      if (cancelled) return;
      window.dispatchEvent(new CustomEvent('social-relationship-updated', { detail: event }));
    });

    for (const eventName of MESSAGE_SOCKET_EVENTS) {
      socket.on(eventName, (payload: unknown) => {
        if (cancelled) return;
        const source =
          payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};
        const sequence = Number(source.sequence ?? 0);
        if (Number.isSafeInteger(sequence) && sequence > 0) {
          const previous = Number(window.sessionStorage.getItem(messageStorageKey) ?? 0);
          if (sequence > previous)
            window.sessionStorage.setItem(messageStorageKey, String(sequence));
        }
        dispatchMessageEvent({ type: eventName, payload });
        if (eventName === 'block.updated') {
          window.dispatchEvent(new CustomEvent('social-relationship-updated', { detail: payload }));
        }
      });
    }

    const handleFocus = () => {
      void synchronize();
      void synchronizeMessages();
      sendClientState();
    };
    const handleVisibility = () => {
      sendClientState();
      if (document.visibilityState === 'visible') {
        void synchronize();
        void synchronizeMessages();
      }
    };
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', sendClientState);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelled = true;
      window.clearTimeout(initialRefreshTimer);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', sendClientState);
      document.removeEventListener('visibilitychange', handleVisibility);
      socket.disconnect();
      if (socketRef.current === socket) socketRef.current = null;
      setIsConnected(false);
    };
  }, [
    accessToken,
    dispatchMessageEvent,
    refreshUnread,
    rememberSequence,
    sendClientState,
    status,
    userId,
  ]);

  const value = useMemo(
    () => ({
      unread,
      refreshVersion,
      isConnected,
      markSeen,
      rememberSequence,
      refreshUnread,
      messageRefreshVersion,
      subscribeToMessages,
      emitMessageSocketEvent,
      setActiveConversation,
    }),
    [
      unread,
      refreshVersion,
      isConnected,
      markSeen,
      rememberSequence,
      refreshUnread,
      messageRefreshVersion,
      subscribeToMessages,
      emitMessageSocketEvent,
      setActiveConversation,
    ],
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}
